"use client"

import { useTranslations } from "next-intl"
import { useAction } from "next-safe-action/hooks"
import { type ReactNode, useEffect, useState } from "react"
import { Button } from "~/components/legacy/common/button"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/legacy/common/popover"
import { ChevronDownIcon } from "~/components/ui/icons"
import { useFilters } from "~/contexts/filter-context"
import { useTrackEvent } from "~/hooks/use-track-event"
import { selectedFilterValues, toggleFilterValue } from "~/lib/catalog-filters"
import { findFilterOptions } from "~/server/web/actions/filters"
import type { ToolFilterSchema } from "~/server/web/tools/schema"

export const ToolFilters = () => {
  const t = useTranslations("tools.filters")
  const { filters, updateFilters } = useFilters<ToolFilterSchema>()
  const { result, execute } = useAction(findFilterOptions)
  const trackEvent = useTrackEvent()
  const labels: Record<string, string> = {
    category: t("all_categories"),
    deliveryModel: t("all_delivery_models"),
    capability: t("all_capabilities"),
    entityType: t("all_entity_types"),
    task: t("all_tasks"),
    language: "All programming languages",
    maintenance: "All code activity",
    accessModel: "All access models",
  }
  const titles: Record<string, string> = {
    category: t("categories"),
    deliveryModel: t("formats"),
    capability: t("features"),
    entityType: t("tool_types"),
    task: t("use_cases"),
    language: "Programming language",
    maintenance: "Code activity",
    accessModel: "Price & access",
  }

  useEffect(execute, [execute])

  return (
    <>
      {result.data?.map(({ type, options }) => (
        <FilterPicker
          key={type}
          value={filters[type]}
          onChange={value => {
            updateFilters({ [type]: value })
            trackEvent("directory.filter_changed", {
              facet: type,
              selection_count: selectedFilterValues(value).length,
            })
          }}
          options={options}
          label={labels[type]}
          title={titles[type]}
          doneLabel={t("done")}
        />
      ))}
    </>
  )
}

function FilterPicker({
  value,
  onChange,
  options,
  label,
  title,
  doneLabel,
}: {
  value?: string
  onChange: (value: string) => void
  options: Array<{ slug: string; name: ReactNode; count: number }>
  label: string
  title: string
  doneLabel: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const selected = selectedFilterValues(value)
  const caption =
    selected.length === 0
      ? label
      : selected.length === 1
        ? (options.find(option => option.slug === selected[0])?.name ?? selected[0])
        : `${title} (${selected.length})`
  const visibleOptions = options.filter(option =>
    String(option.name).toLowerCase().includes(query.toLowerCase()),
  )
  return (
    <Popover
      open={open}
      onOpenChange={next => {
        setOpen(next)
        if (!next) setQuery("")
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="lg"
          aria-label={title}
          className="min-w-36 max-w-64 max-sm:flex-1"
          suffix={<ChevronDownIcon />}
        >
          {caption}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label={title}
        collisionPadding={16}
        className="flex max-h-(--radix-popover-content-available-height) w-72 max-w-[calc(100vw-2rem)] flex-col p-2"
      >
        {title === "Programming language" && (
          <p className="mb-2 px-2 text-xs text-muted-foreground">
            The library's implementation or official bindings. Hosted API client SDKs are listed
            separately on profiles.
          </p>
        )}
        {title === "Code activity" && (
          <p className="mb-2 px-2 text-xs text-muted-foreground">
            Source-code activity is a signal, not a support guarantee. SDK activity does not
            describe a hosted service.
          </p>
        )}
        <input
          aria-label={`Find ${title.toLowerCase()}`}
          placeholder="Search options…"
          value={query}
          onChange={event => setQuery(event.target.value)}
          className="mb-2 w-full shrink-0 rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <button
          type="button"
          onClick={() => onChange("")}
          aria-pressed={selected.length === 0}
          className="mb-1 w-full shrink-0 rounded-md px-2 py-2 text-left text-sm font-medium hover:bg-accent"
        >
          {label}
        </button>
        <div className="min-h-0 max-h-64 overflow-y-auto border-y border-border py-1">
          {!visibleOptions.length && (
            <p className="px-2 py-3 text-sm text-muted-foreground">No matching options</p>
          )}
          {visibleOptions.map(option => (
            <label
              key={option.slug}
              className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm hover:bg-accent"
            >
              <input
                type="checkbox"
                checked={selected.includes(option.slug)}
                onChange={() => onChange(toggleFilterValue(value, option.slug))}
                className="size-4 shrink-0 accent-foreground"
              />
              <span className="flex-1">{option.name}</span>
              <span aria-hidden className="text-xs text-muted-foreground">
                {option.count}
              </span>
            </label>
          ))}
        </div>
        <div className="mt-2 flex shrink-0 items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {selected.length ? `Match any · ${selected.length} selected` : "No restriction"}
          </span>
          <Button size="sm" variant="soft" onClick={() => setOpen(false)}>
            {doneLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
